import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Mail, Rocket } from 'lucide-react';

const LAUNCH_DATE = new Date('2026-10-02T00:00:00');

const getTimeLeft = () => {
  const diff = LAUNCH_DATE.getTime() - Date.now();
  const clamped = Math.max(diff, 0);
  return {
    days: Math.floor(clamped / (1000 * 60 * 60 * 24)),
    hours: Math.floor((clamped / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((clamped / (1000 * 60)) % 60),
    seconds: Math.floor((clamped / 1000) % 60),
    done: diff <= 0,
  };
};


const Invest: React.FC = () => {
  const [time, setTime] = useState(getTimeLeft());
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1200);
  };

  const units = [
    { label: 'Days', value: time.days },
    { label: 'Hours', value: time.hours },
    { label: 'Minutes', value: time.minutes },
    { label: 'Seconds', value: time.seconds },
  ];

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            {/* breadcrumb */}
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
              <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
              <span className="text-white/70">/</span>
              <span className="text-accent-amber text-xs font-semibold">Invest</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-4">
              The Azura Token<br />
              <span className="text-accent-amber">launches October 2.</span>
            </h1>

            <p className="max-w-2xl text-white/70 text-base leading-8">
              AZT is how investors get into Azura: a platform token backed by real-world assets, EVs on the road, battery stations, and operating revenue. The Strategic Round opens on October 2, starting at $0.005 per token.
            </p>
          </div>
        </div>
      </section>

      {/* ══ COUNTDOWN ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Countdown to Token Launch</span>
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-bg-dark tracking-tight mt-3 mb-10">
            AZT Strategic Round opens <span className="text-accent-amber italic">October 2, 2026.</span>
          </h2>

          <div className="grid grid-cols-4 gap-3 md:gap-5 max-w-xl mx-auto mb-8">
            {units.map(u => (
              <div key={u.label} className="bg-bg-dark rounded-2xl py-6 px-2 shadow-card">
                <p className="font-figtree font-black text-3xl md:text-5xl text-accent-amber tabular-nums">{String(u.value).padStart(2, '0')}</p>
                <p className="text-xs text-white/70 uppercase tracking-widest mt-2">{u.label}</p>
              </div>
            ))}
          </div>

          <p className="font-exo text-neutral-500 text-sm max-w-md mx-auto">
            {time.done ? "We're live. The AZT Strategic Round is now open." : 'Get in early — join the list below to be notified the moment the AZT Strategic Round opens.'}
          </p>
        </div>
      </section>

      {/* ══ PITCH NOTE ══ */}
      <section className="py-24 px-6 bg-bg-cream">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center -space-x-1">
              <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
              <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Pitch</span>
          </div>
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-bg-dark tracking-tight mb-8 max-w-2xl">
            How to invest, <span className="text-accent-amber italic">once AZT launches.</span>
          </h2>

          <div className="bg-white border border-border rounded-3xl shadow-card p-8 md:p-12 mb-6">
            <p className="font-exo text-neutral-600 text-base md:text-lg leading-relaxed mb-5">
              AZT is Azura's platform token, and the AZT Strategic Round is where investing actually happens: 100M tokens open at $0.005 each, backed by real-world assets already generating revenue — EVs on the road, battery-swap stations, and daily fleet payments, not a promise on a roadmap.
            </p>
            <p className="font-exo text-neutral-600 text-base md:text-lg leading-relaxed mb-5">
              Nigeria's fuel subsidy removal pushed fuel costs to 30–50% of daily rider income, and Africa's 25 million motorcycles are made almost entirely outside the continent. Azura fixes both problems with one platform: electric fleet operations, a battery-swap network, ride-hailing, and a step-by-step path to local manufacturing — and AZT funds every stage of it.
            </p>
            <p className="font-exo text-neutral-600 text-base md:text-lg leading-relaxed">
              The model is already proven. A $6/day payment per bike, tested in the field in Ibadan, backs every token in circulation, from 500 bikes at launch to a projected 500,000 by 2045. When the Strategic Round opens October 2, this is where you'll come to take part.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {[
              { v: '$0.005', l: 'Strategic Round price' },
              { v: '100M', l: 'AZT in this round' },
              { v: '1B', l: 'Total fixed supply' },
              { v: 'Oct 2', l: 'Round opens' },
            ].map(s => (
              <div key={s.l} className="bg-white border border-border rounded-2xl p-4 text-center shadow-card">
                <p className="font-figtree font-black text-xl text-accent-amber">{s.v}</p>
                <p className="text-xs text-neutral-500 uppercase tracking-widest mt-1">{s.l}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-border shadow-card">
              <img src="/images/bike3.png" alt="Azura fleet on the road" className="w-full h-full object-cover" />
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-border shadow-card">
              <img src="/images/bg1.jpg" alt="Azura operations" className="w-full h-full object-cover" />
            </div>
          </div>

        </div>
      </section>

      {/* ══ NOTIFY / CTA ══ */}
      <section className="py-24 px-6 bg-bg-dark border-t-4 border-accent-amber relative overflow-hidden">
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <span className="text-xs font-bold text-accent-amber uppercase tracking-[0.2em] block mb-5">Be First In</span>
          <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-5 leading-tight">
            Get notified when<br /><span className="text-accent-amber italic">AZT goes live.</span>
          </h2>
          <p className="font-exo text-white/55 text-base leading-relaxed mb-10 max-w-lg mx-auto">
            Leave your email and we'll reach out the moment the AZT Strategic Round opens on October 2, so you can invest from the first block.
          </p>

          {!submitted ? (
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-8">
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
                className="flex-1 bg-white/10 border border-white/15 rounded-full px-5 py-3.5 text-sm font-exo text-white placeholder-white/40 focus:outline-none focus:border-accent-amber transition-all" />
              <button type="submit" disabled={loading}
                className="inline-flex items-center justify-center gap-2 bg-accent-amber text-bg-dark px-6 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all disabled:opacity-60 shrink-0">
                {loading ? <div className="w-4 h-4 border-2 border-bg-dark/30 border-t-bg-dark rounded-full animate-spin" /> : <>Notify Me <Mail size={14} /></>}
              </button>
            </form>
          ) : (
            <div className="flex items-center justify-center gap-2 text-accent-amber font-figtree font-bold text-sm mb-8">
              <CheckCircle2 size={18} /> You're on the list. See you at the AZT launch, October 2.
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/opportunity"
              className="inline-flex items-center gap-2 border-2 border-white/15 text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
              See the Full Opportunity <ArrowRight size={13} />
            </Link>
            <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/15 text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
              White Paper ↗
            </a>
          </div>

          <p className="font-exo text-white/70 text-sm mt-8">
            Questions? Reach us at{' '}
            <a href="mailto:invest@azuramobility.com" className="text-accent-amber hover:text-white transition-colors">invest@azuramobility.com</a>.
          </p>
        </div>
      </section>

    </div>
  );
};

export default Invest;
